/**
 * Custom GroupedBarChartWithStatusTableComponent - Override Component
 * Auto generated overrides from shared library
 */
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GroupedBarChartWithStatusTableComponent as ParentGroupedBarChartWithStatusTableComponent } from '@shared-lib/chart-components/grouped-bar-chart-with-status-table/grouped-bar-chart-with-status-table.component';
import { ChartRendererService } from '../../shared-services/chart-renderer.service';

@Injectable()
class GroupedBarChartWithStatusTableConfigService extends ConfigService {
  static appliedPlannerHighlightRequest: Record<string, any[]> | null = null;

  protected override unwrapResponse(response: any): any {
    if (Array.isArray(response?.responseList) && response.responseList.length > 0) {
      const firstItem = response.responseList[0];
      if (Array.isArray(firstItem?.data)) {
        return this.prioritizeHighlightedRow(this.addEquipmentEngineLabel(firstItem.data));
      }
    }

    if (Array.isArray(response)) {
      return this.prioritizeHighlightedRow(this.addEquipmentEngineLabel(response));
    }

    return response;
  }

  private addEquipmentEngineLabel(rows: any[]): any[] {
    return rows.map((row) => {
      if (!row || typeof row !== 'object') {
        return row;
      }

      const equipment = String(row.equipment ?? '').trim();
      const country = this.toCountryCode(String(row.country ?? row.Country ?? row.region ?? '').trim());
      return {
        ...row,
        equipmentEngineLabel: [equipment, country].filter(Boolean).join(' - ') || equipment || country,
      };
    });
  }

  private toCountryCode(country: string): string {
    const countryCodes: Record<string, string> = {
      australia: 'AUS',
      canada: 'CAN',
      germany: 'DEU',
      india: 'IND',
      us: 'USA',
      usa: 'USA',
      'united states': 'USA',
      'united states of america': 'USA',
      brazil: 'BRA',
      chile: 'CHL',
      peru: 'PER',
      mexico: 'MEX',
      china: 'CHN',
      japan: 'JPN',
      'south africa': 'ZAF',
      indonesia: 'IDN',
    };

    const normalized = country.trim().toLowerCase();
    return normalized ? countryCodes[normalized] || country.slice(0, 3).toUpperCase() : '';
  }

  private prioritizeHighlightedRow(rows: any[]): any[] {
    const request = GroupedBarChartWithStatusTableConfigService.appliedPlannerHighlightRequest;
    if (!request || !rows.length) {
      return rows;
    }

    const matchIndex = rows.findIndex((row) => this.matchesPlannerHighlight(row, request));
    if (matchIndex < 0) {
      return rows;
    }

    const nextRows = rows.map((row, index) => index === matchIndex
      ? { ...row, __patPlannerHighlighted: true }
      : row);
    const [matchedRow] = nextRows.splice(matchIndex, 1);
    nextRows.unshift(matchedRow);
    return nextRows;
  }

  private matchesPlannerHighlight(row: any, requestParams: Record<string, any[]>): boolean {
    const keys = ['customer_name', 'equipment', 'engine', 'emission'];
    return keys.every((key) => {
      const requestedValues = this.toNormalizedSet(requestParams[key]);
      if (!requestedValues.size) {
        return true;
      }

      const rowValue = this.resolvePlannerHighlightRowValue(row, key);
      return requestedValues.has(this.normalizeHighlightValue(rowValue));
    });
  }

  private resolvePlannerHighlightRowValue(row: any, key: string): any {
    const aliases: Record<string, string[]> = {
      customer_name: ['customer_name', 'customerName', 'customer'],
      equipment: ['equipment', 'machine_model', 'machineModel'],
      engine: ['engine', 'engine_model', 'engineModel', 'engine_model_key'],
      emission: ['emission', 'emission_key', 'certification'],
    };
    const paths = aliases[key] || [key];
    for (const path of paths) {
      const value = String(path)
        .split('.')
        .reduce((current: any, part: string) => current?.[part], row);
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }
    return undefined;
  }

  private toNormalizedSet(value: any): Set<string> {
    const values = Array.isArray(value) ? value.flat(Infinity) : [value];
    return new Set(values.map((item) => this.normalizeHighlightValue(item)).filter(Boolean));
  }

  private normalizeHighlightValue(value: any): string {
    return String(Array.isArray(value) ? value[0] ?? '' : value ?? '').trim().toLowerCase();
  }
}

@Component({
  selector: 'app-grouped-bar-chart-with-status-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './grouped-bar-chart-with-status-table.component.html',
  styleUrls: ['./grouped-bar-chart-with-status-table.component.scss'],
  providers: [
    {
      provide: ConfigService,
      useClass: GroupedBarChartWithStatusTableConfigService,
    },
  ],
})
export class GroupedBarChartWithStatusTableComponent extends ParentGroupedBarChartWithStatusTableComponent implements OnInit {
  // Constructor is inherited from parent to avoid dependency injection issues

  ngOnInit(): void {
    const originalSetup = (this.wrapper as any).setupChartClickEvent;

    (this.wrapper as any).setupChartClickEvent = () => {
      const chart = (this.wrapper as any).callbacks?.getChart();
      if (!chart) return;

      const panelConfig = this.data?.chartConfig?.plannerPanel;
      if (!panelConfig) {
        if (originalSetup) {
          originalSetup.call(this.wrapper);
        }
        return;
      }

      chart.off('click');
      chart.on('click', (params: any) => {
        if (params?.componentType === 'series') {
          const sourceRow = params?.data?.raw || null;
          const requestParams = this.buildPlannerPanelRequestData(sourceRow, panelConfig);
          this.pendingPlannerHighlightRequest = this.cloneRequestParams(requestParams);
          window.dispatchEvent(new CustomEvent('pat-price-planner-open', {
            detail: {
              chartWidgetId: panelConfig.chartWidgetId || this.data?.widgetId,
              plannerWidgetId: panelConfig.plannerWidgetId || 'widget-price-planner',
              chartWidth: panelConfig.chartWidth ?? 8,
              plannerWidth: panelConfig.plannerWidth ?? 4,
              requestFieldMap: panelConfig.requestFieldMap || null,
              requestParams,
              sourceRow,
              category: params?.name,
              seriesName: params?.seriesName,
            },
          }));
          return;
        }

        (this.wrapper as any).handleCategoryChartClick(params);
      });
    };
  }
  @ViewChild('statusPopoverHost', { read: ViewContainerRef })
  private statusPopoverHost?: ViewContainerRef;

  activeStatusPopover: { row: any; cell: any; style: Record<string, string> } | null = null;

  private readonly hoverConfigService = inject(ConfigService);
  private readonly chartRendererService = inject(ChartRendererService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly hoverConfigCache = new Map<string, any>();
  private hideTimer?: ReturnType<typeof setTimeout>;
  private renderToken = 0;
  private plannerClickHandler?: (params: any) => void;
  private plannerAttachTimer?: ReturnType<typeof setTimeout>;
  private customComparisonFrame = 0;
  private customComparisonFinishedHandler?: () => void;
  private applyingCustomComparisonOption = false;
  private plannerAppliedReloadTimer?: ReturnType<typeof setTimeout>;
  private pendingPlannerHighlightRequest: Record<string, any[]> | null = null;
  private appliedPlannerHighlightRequest: Record<string, any[]> | null = null;
  private readonly plannerAppliedHandler = (event: Event) => this.handlePlannerApplied(event as CustomEvent);

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.schedulePlannerOpenHandlerAttach();
    this.attachCustomComparisonRenderer();
    window.addEventListener('pat-price-planner-applied', this.plannerAppliedHandler as EventListener);
  }

  override selectView(index: any): any {
    const result = super.selectView(index);
    this.schedulePlannerOpenHandlerAttach();
    this.scheduleCustomComparisonGraphics();
    return result;
  }

  override onPageStartChange(value: Event): void {
    super.onPageStartChange(value);
    this.scheduleCustomComparisonGraphics();
  }

  override isCategorySelected(category: any): any {
    // TODO: Add your custom logic here
    return super.isCategorySelected(category);
  }

  override getStatusStyle(status: any): any {
    // TODO: Add your custom logic here
    return super.getStatusStyle(status);
  }

  hasStatusHover(row: any): boolean {
    return !!this.getStatusHoverConfigUrl(this.resolveStatusRowConfig(row));
  }

  showStatusPopover(event: MouseEvent, row: any, cell: any): void {
    const rowConfig = this.resolveStatusRowConfig(row);
    const configUrl = this.getStatusHoverConfigUrl(rowConfig);

    if (!configUrl && !rowConfig?.hoverWidget && !rowConfig?.hoverDashboardConfig) {
      this.hideStatusPopover();
      return;
    }

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = undefined;
    }

    const panel = rowConfig?.hoverPanel || {};
    this.activeStatusPopover = {
      row,
      cell,
      style: this.buildPopoverStyle(event, panel),
    };
    this.cdr.detectChanges();
    if (rowConfig?.hoverWidget || rowConfig?.hoverDashboardConfig) {
      this.renderHoverWidget(rowConfig.hoverDashboardConfig || { pageContent: [{ columns: [rowConfig.hoverWidget] }] }, rowConfig, row, cell, ++this.renderToken);
      return;
    }

    this.renderStatusPopover(configUrl, rowConfig, row, cell);
  }

  keepStatusPopoverOpen(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = undefined;
    }
  }

  scheduleHideStatusPopover(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }

    this.hideTimer = setTimeout(() => this.hideStatusPopover(), 120);
  }

  hideStatusPopover(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = undefined;
    }

    this.activeStatusPopover = null;
    this.statusPopoverHost?.clear();
  }

  override ngOnDestroy(): void {
    this.hideStatusPopover();
    if (this.plannerAttachTimer) {
      clearTimeout(this.plannerAttachTimer);
      this.plannerAttachTimer = undefined;
    }
    if (this.customComparisonFrame) {
      cancelAnimationFrame(this.customComparisonFrame);
      this.customComparisonFrame = 0;
    }
    if (this.plannerAppliedReloadTimer) {
      clearTimeout(this.plannerAppliedReloadTimer);
      this.plannerAppliedReloadTimer = undefined;
    }
    this.detachCustomComparisonRenderer();
    this.detachPlannerOpenHandler();
    window.removeEventListener('pat-price-planner-applied', this.plannerAppliedHandler as EventListener);
    super.ngOnDestroy();
  }

  private attachCustomComparisonRenderer(attempt = 0): void {
    const chart = this.resolveChartInstance();
    if (!chart) {
      if (attempt < 20) {
        setTimeout(() => this.attachCustomComparisonRenderer(attempt + 1), attempt === 0 ? 0 : 100);
      }
      return;
    }

    this.detachCustomComparisonRenderer();
    this.customComparisonFinishedHandler = () => {
      if (!this.applyingCustomComparisonOption) {
        this.scheduleCustomComparisonGraphics();
      }
    };
    chart.on('finished', this.customComparisonFinishedHandler);
    this.scheduleCustomComparisonGraphics();
  }

  private detachCustomComparisonRenderer(): void {
    if (!this.customComparisonFinishedHandler) {
      return;
    }

    const chart = this.resolveChartInstance();
    chart?.off('finished', this.customComparisonFinishedHandler);
    this.customComparisonFinishedHandler = undefined;
  }

  private scheduleCustomComparisonGraphics(): void {
    if (this.customComparisonFrame) {
      cancelAnimationFrame(this.customComparisonFrame);
    }

    this.customComparisonFrame = requestAnimationFrame(() => {
      this.customComparisonFrame = 0;
      this.renderCustomComparisonGraphics();
    });
  }

  private renderCustomComparisonGraphics(): void {
    const chart = this.resolveChartInstance();
    const config = this.data?.chartConfig || {};
    const comparisons = this.resolveCustomComparisonConfigs(config);
    if (!chart) {
      return;
    }

    const option = chart.getOption() as any;
    const seriesOptions = Array.isArray(option?.series) ? option.series : [];
    const rows = this.resolveRenderedRows(seriesOptions);
    this.ensureLegendNoteHeadroom(chart, config, option);
    const highlightGraphics = this.buildAppliedPlannerHighlightGraphics(chart, rows, config, option);
    const legendNoteGraphics = this.buildLegendNoteGraphic(chart, config, option);

    if (!comparisons.length) {
      this.replaceCustomGraphics(chart, [...highlightGraphics, ...legendNoteGraphics]);
      return;
    }

    const seriesConfigs = this.resolveCustomSeriesConfigs(config);
    if (!rows.length || seriesConfigs.length < 2) {
      this.replaceCustomGraphics(chart, [...highlightGraphics, ...legendNoteGraphics]);
      return;
    }

    this.applyingCustomComparisonOption = true;
    this.ensureCustomYAxisHeadroom(chart, seriesOptions, comparisons, config, option);

    const barSeriesCount = seriesConfigs.length;
    const rawBarWidth = Number(config.barWidth ?? 16);
    const barWidth = Number.isFinite(rawBarWidth) ? rawBarWidth : 16;
    const gap = Number(config.seriesGap ?? 6);
    const graphics: any[] = [];
    const labelGraphics: any[] = [];
    const occupiedLabelRectsByRow = new Map<number, Array<{ x: number; y: number; width: number; height: number }>>();

    comparisons.forEach((comparison, comparisonIndex) => {
      const baseIndex = this.resolveCustomSeriesIndex(seriesConfigs, comparison?.baseSeries, 0);
      const compareIndex = this.resolveCustomSeriesIndex(seriesConfigs, comparison?.compareSeries, 1);
      const baseSeries = seriesConfigs[baseIndex];
      const compareSeries = seriesConfigs[compareIndex];

      if (!baseSeries || !compareSeries || baseIndex === compareIndex) {
        return;
      }

      const lineColor = comparison?.lineColor || '#111111';
      const bubbleFill = comparison?.bubbleBackground || '#ffffff';
      const bubbleText = comparison?.textColor || '#111111';
      const bubbleBorder = comparison?.bubbleBorderColor || '#111111';
      const bubbleHeight = Number(comparison?.bubbleHeight ?? 22);
      const bubbleFontSize = Number(comparison?.bubbleFontSize ?? 11);
      const bubbleHorizontalPadding = Number(comparison?.bubbleHorizontalPadding ?? 18);
      const minBubbleWidth = Number(comparison?.minBubbleWidth ?? 44);
      const stackGap = Number(comparison?.stackGap ?? config.comparisonStackGap ?? 28);
      const lineLift = Number(comparison?.lineLift ?? (26 + (comparisonIndex * stackGap)));
      const stemGap = Number(comparison?.stemGap ?? 8);
      const arrowSize = Number(comparison?.arrowSize ?? 5);
      const decimals = Number(comparison?.valueDecimals ?? 1);
      const prefix = String(comparison?.valuePrefix ?? '');
      const suffix = String(comparison?.valueSuffix ?? '%');
      const minBubbleTop = Number(comparison?.minBubbleTop ?? 4);
      const comparisonGap = Number(comparison?.seriesGap ?? gap);
      const groupWidth = (barSeriesCount * barWidth) + (Math.max(0, barSeriesCount - 1) * comparisonGap);

      rows.forEach((row, index) => {
        const baseValue = Number(this.resolveCustomPath(row, baseSeries.valueField));
        const compareValue = Number(this.resolveCustomPath(row, compareSeries.valueField));
        if (!Number.isFinite(baseValue) || !Number.isFinite(compareValue) || baseValue === 0) {
          return;
        }

        const configuredPercent = this.resolveComparisonValue(row, comparison);
        const percent = configuredPercent !== undefined
          ? Number(configuredPercent)
          : ((compareValue - baseValue) / baseValue) * 100;
        if (!Number.isFinite(percent)) {
          return;
        }

        const centerX = Number(chart.convertToPixel({ xAxisIndex: 0 }, index));
        const basePoint = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [index, baseValue]) as number[];
        const comparePoint = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [index, compareValue]) as number[];
        if (!Number.isFinite(centerX) || !Array.isArray(basePoint) || !Array.isArray(comparePoint)) {
          return;
        }

        const baseX = centerX - (groupWidth / 2) + (baseIndex * (barWidth + comparisonGap)) + (barWidth / 2);
        const compareX = centerX - (groupWidth / 2) + (compareIndex * (barWidth + comparisonGap)) + (barWidth / 2);
        const topY = Math.min(basePoint[1], comparePoint[1]);
        const minLineY = minBubbleTop + bubbleHeight + 8;
        const maxLineY = topY - arrowSize - stemGap - 4;
        let lineY = Math.min(Math.max(topY - lineLift, minLineY), maxLineY);
        if (!Number.isFinite(lineY) || lineY >= topY - arrowSize) {
          return;
        }

        const bubbleTextValue = `${percent > 0 ? '+' : ''}${percent.toFixed(decimals)}${suffix}`;
        const label = comparison?.labelKey
          ? String(this.resolveCustomPath(row, comparison.labelKey) ?? bubbleTextValue)
          : `${prefix}${bubbleTextValue}`;
        const bubbleWidth = Math.max(minBubbleWidth, Math.round((label.length * (bubbleFontSize * 0.65)) + bubbleHorizontalPadding));
        const chartWidth = chart.getWidth?.() ?? 0;
        const centerBubbleX = (baseX + compareX) / 2;
        const initialBubbleX = chartWidth > 0
          ? Math.max((bubbleWidth / 2) + 4, Math.min(centerBubbleX, chartWidth - (bubbleWidth / 2) - 4))
          : centerBubbleX;
        let bubbleY = lineY - bubbleHeight - 6;
        const occupiedRects = occupiedLabelRectsByRow.get(index) || [];
        bubbleY = this.resolveCollisionFreeBubbleY({
          initialBubbleY: bubbleY,
          bubbleX: initialBubbleX,
          bubbleWidth,
          bubbleHeight,
          occupiedRects,
          minBubbleY: minBubbleTop,
          maxBubbleY: maxLineY - bubbleHeight - 6,
        });
        lineY = bubbleY + bubbleHeight + 6;
        const bubbleX = this.resolveCollisionFreeBubbleX({
          initialBubbleX,
          bubbleY,
          bubbleWidth,
          bubbleHeight,
          centerX,
          groupWidth,
          barSeriesCount,
          barWidth,
          gap: comparisonGap,
          baseIndex,
          compareIndex,
          row,
          seriesConfigs,
          chart,
          chartWidth,
        });
        occupiedLabelRectsByRow.set(index, [
          ...occupiedRects,
          {
            x: bubbleX - (bubbleWidth / 2),
            y: bubbleY,
            width: bubbleWidth,
            height: bubbleHeight,
          },
        ]);
        const lineBottomY = lineY + 2;

        graphics.push({
          type: 'group',
          silent: true,
          z: 12,
          children: [
            {
              type: 'line',
              shape: { x1: baseX, y1: lineY, x2: compareX, y2: lineY },
              style: { stroke: lineColor, lineWidth: 1.25 },
            },
            {
              type: 'line',
              shape: { x1: baseX, y1: lineBottomY, x2: baseX, y2: basePoint[1] },
              style: { stroke: lineColor, lineWidth: 1.25 },
            },
            {
              type: 'line',
              shape: { x1: compareX, y1: lineBottomY, x2: compareX, y2: comparePoint[1] - stemGap },
              style: { stroke: lineColor, lineWidth: 1.25 },
            },
            {
              type: 'polygon',
              shape: {
                points: [
                  [compareX, comparePoint[1]],
                  [compareX - arrowSize, comparePoint[1] - arrowSize - 2],
                  [compareX + arrowSize, comparePoint[1] - arrowSize - 2],
                ],
              },
              style: { fill: lineColor },
            },
          ],
        });

        labelGraphics.push({
          type: 'group',
          silent: true,
          z: 20,
          children: [
            {
              type: 'rect',
              shape: {
                x: bubbleX - (bubbleWidth / 2),
                y: bubbleY,
                width: bubbleWidth,
                height: bubbleHeight,
                r: bubbleHeight / 2,
              },
              style: {
                fill: bubbleFill,
                stroke: bubbleBorder,
                lineWidth: 1,
              },
            },
            {
              type: 'text',
              style: {
                text: label,
                x: bubbleX,
                y: bubbleY + (bubbleHeight / 2),
                fill: bubbleText,
                fontSize: bubbleFontSize,
                fontWeight: 700,
                align: 'center',
                verticalAlign: 'middle',
              },
            },
          ],
        });
      });
    });

    this.replaceCustomGraphics(chart, [...highlightGraphics, ...legendNoteGraphics, ...graphics, ...labelGraphics]);
    requestAnimationFrame(() => {
      this.applyingCustomComparisonOption = false;
    });
  }

  private replaceCustomGraphics(chart: echarts.ECharts, graphic: any[]): void {
    chart.setOption({ graphic }, { replaceMerge: ['graphic'] });
  }

  private handlePlannerApplied(event: CustomEvent): void {
    const detail = event?.detail || {};
    const listenerWidgets = Array.isArray(detail.listenerWidgets)
      ? detail.listenerWidgets.map((item: unknown) => String(item || '').trim()).filter(Boolean)
      : [];
    const widgetId = String(this.data?.widgetId || 'widget-grouped-bar-chart-with-status-table');

    if (listenerWidgets.length && !listenerWidgets.includes(widgetId)) {
      return;
    }

    this.appliedPlannerHighlightRequest = this.cloneRequestParams(this.pendingPlannerHighlightRequest);
    GroupedBarChartWithStatusTableConfigService.appliedPlannerHighlightRequest = this.cloneRequestParams(this.pendingPlannerHighlightRequest);
    this.reloadAfterPlannerApplied();
    this.scheduleCustomComparisonGraphics();
  }

  private reloadAfterPlannerApplied(): void {
    if (this.plannerAppliedReloadTimer) {
      clearTimeout(this.plannerAppliedReloadTimer);
    }

    this.plannerAppliedReloadTimer = setTimeout(() => {
      this.plannerAppliedReloadTimer = undefined;
      const wrapperAny = this.wrapper as any;
      const currentFilters = wrapperAny?.globalFilterService?.getCurrentFilters?.() || {};
      const filters = {
        ...currentFilters,
        queryParams: {
          ...(currentFilters?.queryParams || {}),
          _plannerRefreshTs: Date.now(),
        },
      };
      wrapperAny?.callbacks?.onReload?.(filters);
    }, 150);
  }

  private buildAppliedPlannerHighlightGraphics(chart: echarts.ECharts, rows: any[], config: any, option: any): any[] {
    if (!this.appliedPlannerHighlightRequest || !rows.length) {
      return [];
    }

    const rowIndex = rows.findIndex((row) => this.matchesPlannerHighlight(row, this.appliedPlannerHighlightRequest));
    if (rowIndex < 0) {
      return [];
    }

    const centerX = Number(chart.convertToPixel({ xAxisIndex: 0 }, rowIndex));
    if (!Number.isFinite(centerX)) {
      return [];
    }

    const prevCenter = rowIndex > 0 ? Number(chart.convertToPixel({ xAxisIndex: 0 }, rowIndex - 1)) : NaN;
    const nextCenter = rowIndex < rows.length - 1 ? Number(chart.convertToPixel({ xAxisIndex: 0 }, rowIndex + 1)) : NaN;
    const configuredSlotWidth = Number(config.categorySlotWidth ?? 148);
    const measuredSlotWidth = Number.isFinite(nextCenter)
      ? Math.abs(nextCenter - centerX)
      : Number.isFinite(prevCenter)
        ? Math.abs(centerX - prevCenter)
        : configuredSlotWidth;
    const slotWidth = Math.max(48, Number.isFinite(measuredSlotWidth) ? measuredSlotWidth : configuredSlotWidth);
    const grid = Array.isArray(option?.grid) ? option.grid[0] : option?.grid || {};
    const chartHeight = Number(chart.getHeight?.() || 0);
    const top = this.resolvePixelSize(grid?.top, chartHeight, Number(config.gridTop ?? 52));
    const bottom = this.resolvePixelSize(grid?.bottom, chartHeight, Number(config.gridBottom ?? 24));
    const height = Math.max(40, chartHeight - top - bottom);
    const x = centerX - (slotWidth / 2) + 4;
    const y = top;
    const width = Math.max(40, slotWidth - 8);
    const fill = String(config.appliedHighlightBackground || 'rgba(250, 204, 21, 0.18)');
    const stroke = String(config.appliedHighlightBorderColor || '#f59e0b');

    return [
      {
        type: 'group',
        silent: true,
        z: 2,
        children: [
          {
            type: 'rect',
            shape: { x, y, width, height, r: Number(config.appliedHighlightRadius ?? 8) },
            style: {
              fill,
              stroke,
              lineWidth: Number(config.appliedHighlightBorderWidth ?? 1.4),
              lineDash: config.appliedHighlightLineDash || [6, 4],
            },
          },
          {
            type: 'text',
            silent: true,
            style: {
              text: String(config.appliedHighlightLabel || 'Updated'),
              x: x + (width / 2),
              y: y + 14,
              fill: String(config.appliedHighlightTextColor || '#92400e'),
              fontSize: Number(config.appliedHighlightFontSize ?? 11),
              fontWeight: 800,
              align: 'center',
              verticalAlign: 'middle',
            },
          },
        ],
      },
    ];
  }

  private buildLegendNoteGraphic(chart: echarts.ECharts, config: any, option: any): any[] {
    const legendNote = String(config.legendNote || '').trim();
    if (!legendNote) {
      return [];
    }

    const chartWidth = Number(chart.getWidth?.() || 0);
    const chartHeight = Number(chart.getHeight?.() || 0);
    if (!chartWidth || !chartHeight) {
      return [];
    }

    const grid = Array.isArray(option?.grid) ? option.grid[0] : option?.grid || {};
    const legendTop = this.resolvePixelSize(config.legendTop, chartHeight, 0);
    const noteFontSize = Number(config.legendNoteFontSize ?? 13);
    const noteColor = String(config.legendNoteColor || '#64748b');
    const stackBelowWidth = Number(config.legendNoteStackBelowWidth ?? 1100);
    const shouldStackBelowLegend = chartWidth < stackBelowWidth;
    const noteY = shouldStackBelowLegend
      ? legendTop + Number(config.legendNoteStackedTopOffset ?? 24)
      : legendTop + Number(config.legendNoteTopOffset ?? 5);
    const noteX = shouldStackBelowLegend
      ? Number(config.legendNoteStackedLeft ?? 0)
      : chartWidth - Number(config.legendNoteRight ?? 30);
    const noteMaxWidth = shouldStackBelowLegend
      ? Math.max(160, chartWidth - Number(config.legendNoteStackedRightPadding ?? 24))
      : Math.max(200, chartWidth * Number(config.legendNoteMaxWidthRatio ?? 0.3));

    return [
      {
        type: 'text',
        silent: true,
        z: 10,
        style: {
          text: legendNote,
          x: noteX,
          y: noteY,
          fill: noteColor,
          fontSize: noteFontSize,
          fontWeight: Number(config.legendNoteFontWeight ?? 700),
          align: shouldStackBelowLegend ? 'left' : 'right',
          verticalAlign: 'top',
          maxWidth: noteMaxWidth,
        },
      },
    ];
  }

  private ensureLegendNoteHeadroom(chart: echarts.ECharts, config: any, option: any): void {
    const legendNote = String(config.legendNote || '').trim();
    if (!legendNote) {
      return;
    }

    const chartWidth = Number(chart.getWidth?.() || 0);
    if (!chartWidth || chartWidth >= Number(config.legendNoteStackBelowWidth ?? 1100)) {
      return;
    }

    const grid = Array.isArray(option?.grid) ? option.grid[0] : option?.grid || {};
    const currentTop = Number(grid?.top ?? config.gridTop ?? 52);
    const stackedTop = Number(config.legendNoteStackedGridTop ?? 76);
    if (!Number.isFinite(currentTop) || currentTop >= stackedTop) {
      return;
    }

    chart.setOption({ grid: { top: stackedTop } }, false);
  }

  private matchesPlannerHighlight(row: any, requestParams: Record<string, any[]> | null): boolean {
    if (!row || !requestParams) {
      return false;
    }

    const keys = ['customer_name', 'equipment', 'engine', 'emission'];
    return keys.every((key) => {
      const requestedValues = this.toNormalizedSet(requestParams[key]);
      if (!requestedValues.size) {
        return true;
      }

      const rowValue = this.resolvePlannerHighlightRowValue(row, key);
      return requestedValues.has(this.normalizeHighlightValue(rowValue));
    });
  }

  private resolvePlannerHighlightRowValue(row: any, key: string): any {
    const aliases: Record<string, string[]> = {
      customer_name: ['customer_name', 'customerName', 'customer'],
      equipment: ['equipment', 'machine_model', 'machineModel'],
      engine: ['engine', 'engine_model', 'engineModel', 'engine_model_key'],
      emission: ['emission', 'emission_key', 'certification'],
    };
    const paths = aliases[key] || [key];
    for (const path of paths) {
      const value = this.resolveCustomPath(row, path);
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }
    return undefined;
  }

  private toNormalizedSet(value: any): Set<string> {
    return new Set(
      this.toRequestArray(value)
        .map((item) => this.normalizeHighlightValue(item))
        .filter(Boolean),
    );
  }

  private normalizeHighlightValue(value: any): string {
    return String(Array.isArray(value) ? value[0] ?? '' : value ?? '').trim().toLowerCase();
  }

  private resolvePixelSize(value: any, total: number, fallback: number): number {
    if (typeof value === 'string' && value.trim().endsWith('%')) {
      const percent = Number(value.replace('%', ''));
      return Number.isFinite(percent) ? (total * percent) / 100 : fallback;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  }

  private resolveCollisionFreeBubbleX(params: {
    initialBubbleX: number;
    bubbleY: number;
    bubbleWidth: number;
    bubbleHeight: number;
    centerX: number;
    groupWidth: number;
    barSeriesCount: number;
    barWidth: number;
    gap: number;
    baseIndex: number;
    compareIndex: number;
    row: any;
    seriesConfigs: any[];
    chart: echarts.ECharts;
    chartWidth: number;
  }): number {
    let bubbleX = params.initialBubbleX;
    const padding = 8;
    const baseline = params.chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [0, 0]) as number[];
    const baselineY = Array.isArray(baseline) ? baseline[1] : Number.POSITIVE_INFINITY;
    const bubbleTop = params.bubbleY;
    const bubbleBottom = params.bubbleY + params.bubbleHeight;

    for (let seriesIndex = 0; seriesIndex < params.barSeriesCount; seriesIndex += 1) {
      if (seriesIndex === params.baseIndex || seriesIndex === params.compareIndex) {
        continue;
      }

      const series = params.seriesConfigs[seriesIndex];
      const value = Number(this.resolveCustomPath(params.row, series?.valueField));
      if (!Number.isFinite(value)) {
        continue;
      }

      const point = params.chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [0, value]) as number[];
      if (!Array.isArray(point) || !Number.isFinite(point[1])) {
        continue;
      }

      const barTop = Math.min(point[1], baselineY);
      const barBottom = Math.max(point[1], baselineY);
      const overlapsVertically = bubbleBottom > barTop && bubbleTop < barBottom;
      if (!overlapsVertically) {
        continue;
      }

      const barX = params.centerX - (params.groupWidth / 2) + (seriesIndex * (params.barWidth + params.gap)) + (params.barWidth / 2);
      const barLeft = barX - (params.barWidth / 2) - padding;
      const barRight = barX + (params.barWidth / 2) + padding;
      const bubbleLeft = bubbleX - (params.bubbleWidth / 2);
      const bubbleRight = bubbleX + (params.bubbleWidth / 2);
      const overlapsHorizontally = bubbleRight > barLeft && bubbleLeft < barRight;

      if (!overlapsHorizontally) {
        continue;
      }

      bubbleX = bubbleX <= barX
        ? barLeft - (params.bubbleWidth / 2)
        : barRight + (params.bubbleWidth / 2);
    }

    if (params.chartWidth > 0) {
      return Math.max((params.bubbleWidth / 2) + 4, Math.min(bubbleX, params.chartWidth - (params.bubbleWidth / 2) - 4));
    }

    return bubbleX;
  }

  private resolveCollisionFreeBubbleY(params: {
    initialBubbleY: number;
    bubbleX: number;
    bubbleWidth: number;
    bubbleHeight: number;
    occupiedRects: Array<{ x: number; y: number; width: number; height: number }>;
    minBubbleY: number;
    maxBubbleY: number;
  }): number {
    const gap = 5;
    const bubbleLeft = params.bubbleX - (params.bubbleWidth / 2);
    const bubbleRight = params.bubbleX + (params.bubbleWidth / 2);
    const overlaps = (y: number) => params.occupiedRects.find((rect) => {
      const overlapsHorizontally = bubbleRight > rect.x && bubbleLeft < rect.x + rect.width;
      const overlapsVertically = y + params.bubbleHeight + gap > rect.y && y < rect.y + rect.height + gap;
      return overlapsHorizontally && overlapsVertically;
    });

    let bubbleY = Math.min(Math.max(params.initialBubbleY, params.minBubbleY), params.maxBubbleY);
    for (let attempt = 0; attempt < params.occupiedRects.length + 2; attempt += 1) {
      const collision = overlaps(bubbleY);
      if (!collision) {
        return bubbleY;
      }

      const upwardY = collision.y - params.bubbleHeight - gap;
      if (upwardY >= params.minBubbleY) {
        bubbleY = upwardY;
        continue;
      }

      const downwardY = collision.y + collision.height + gap;
      if (downwardY <= params.maxBubbleY) {
        bubbleY = downwardY;
        continue;
      }

      return Math.max(params.minBubbleY, Math.min(params.maxBubbleY, bubbleY));
    }

    return bubbleY;
  }

  private ensureCustomYAxisHeadroom(chart: echarts.ECharts, seriesOptions: any[], comparisons: any[], config: any, option: any): void {
    if (config.autoComparisonHeadroom === false) {
      return;
    }

    const maxValue = seriesOptions.reduce((max, series) => {
      if (series?.type !== 'bar' || !Array.isArray(series.data)) {
        return max;
      }

      const seriesMax = series.data.reduce((innerMax: number, point: any) => {
        const value = Number(Array.isArray(point?.value) ? point.value[1] : point?.value ?? point);
        return Number.isFinite(value) ? Math.max(innerMax, value) : innerMax;
      }, 0);
      return Math.max(max, seriesMax);
    }, 0);

    if (!Number.isFinite(maxValue) || maxValue <= 0) {
      return;
    }

    const configuredMax = Number(config.yAxisMax);
    const headroom = comparisons.reduce((max, comparison) => {
      const value = Number(comparison?.headroomRatio ?? config.comparisonHeadroomRatio ?? 0.2);
      return Number.isFinite(value) ? Math.max(max, value) : max;
    }, 0.2);
    const rawNextMax = Math.max(Number.isFinite(configuredMax) ? configuredMax : 0, maxValue * (1 + Math.max(0, headroom)));
    const interval = Number(config.yAxisInterval);
    const nextMax = Number.isFinite(interval) && interval > 0
      ? Math.ceil(rawNextMax / interval) * interval
      : rawNextMax;
    const currentYAxis = Array.isArray(option?.yAxis) ? option.yAxis[0] : option?.yAxis;
    const currentMax = Number(currentYAxis?.max);

    if (Math.abs((Number.isFinite(currentMax) ? currentMax : 0) - nextMax) < 1) {
      return;
    }

    chart.setOption({ yAxis: { max: nextMax } }, false);
  }

  private resolveCustomComparisonConfigs(config: any): any[] {
    if (config?.comparisons === false) {
      return [];
    }

    return Array.isArray(config?.comparisons) ? config.comparisons.filter(Boolean) : [];
  }

  private resolveCustomSeriesConfigs(config: any): any[] {
    const series = Array.isArray(config?.series) ? config.series : [];
    return series.map((entry: any, index: number) => ({
      id: String(entry.id || entry.key || entry.valueField || `series-${index}`),
      name: String(entry.name || entry.label || entry.valueField || `Series ${index + 1}`),
      valueField: String(entry.valueField || entry.key || ''),
    }));
  }

  private resolveRenderedRows(seriesOptions: any[]): any[] {
    const firstSeries = seriesOptions.find((series) => Array.isArray(series?.data));
    if (!firstSeries) {
      return [];
    }

    return firstSeries.data
      .map((point: any) => point?.raw || point?.data?.raw || null)
      .filter((row: any) => row && typeof row === 'object');
  }

  private resolveCustomSeriesIndex(seriesConfigs: any[], selector: any, fallbackIndex: number): number {
    if (Number.isInteger(selector)) {
      const index = Number(selector);
      return index >= 0 && index < seriesConfigs.length ? index : fallbackIndex;
    }

    const normalized = String(selector ?? '').trim().toLowerCase();
    if (!normalized) {
      return fallbackIndex;
    }

    const matchIndex = seriesConfigs.findIndex((series, index) => {
      return (
        String(series.id).toLowerCase() === normalized ||
        String(series.name).toLowerCase() === normalized ||
        String(series.valueField).toLowerCase() === normalized ||
        String(index).toLowerCase() === normalized
      );
    });

    return matchIndex >= 0 ? matchIndex : fallbackIndex;
  }

  private resolveCustomPath(source: any, path: string): any {
    if (!source || !path) {
      return undefined;
    }

    return String(path)
      .split('.')
      .reduce((current: any, key: string) => current?.[key], source);
  }

  private resolveComparisonValue(row: any, comparison: any): any {
    const candidates = [
      ...(Array.isArray(comparison?.valueKeys) ? comparison.valueKeys : []),
      comparison?.valueKey,
      ...(Array.isArray(comparison?.fallbackValueKeys) ? comparison.fallbackValueKeys : []),
    ].filter((key) => key !== undefined && key !== null && String(key).trim() !== '');

    for (const key of candidates) {
      const value = this.resolveCustomPath(row, String(key));
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return undefined;
  }

  private buildPlannerPanelRequestData(sourceRow: any, panelConfig: any): Record<string, any[]> {
    if (!sourceRow || typeof sourceRow !== 'object') {
      return {};
    }

    const fieldMap = panelConfig?.requestFieldMap && typeof panelConfig.requestFieldMap === 'object'
      ? panelConfig.requestFieldMap
      : {
          customer_name: 'customer_name',
          equipment: 'equipment',
          engine: 'engine',
          emission: 'emission',
        };
    const requestData: Record<string, any[]> = {};

    Object.keys(fieldMap).forEach((targetKey) => {
      const mappedPath = String(fieldMap[targetKey] || targetKey);
      const values = this.toRequestArray(this.resolveCustomPath(sourceRow, mappedPath));
      if (values.length) {
        requestData[targetKey] = values;
      }
    });

    return requestData;
  }

  private cloneRequestParams(value: Record<string, any[]> | null): Record<string, any[]> | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    return Object.keys(value).reduce<Record<string, any[]>>((acc, key) => {
      acc[key] = this.toRequestArray(value[key]);
      return acc;
    }, {});
  }

  private resolveChartInstance(): echarts.ECharts | null {
    const chartHost = this.hostRef.nativeElement.querySelector('.chart-host') as HTMLDivElement | null;
    return chartHost ? echarts.getInstanceByDom(chartHost) ?? null : null;
  }

  private schedulePlannerOpenHandlerAttach(attempt = 0): void {
    if (!this.data?.chartConfig?.plannerPanel) {
      return;
    }

    if (this.plannerAttachTimer) {
      clearTimeout(this.plannerAttachTimer);
    }

    this.plannerAttachTimer = setTimeout(() => {
      const attached = this.attachPlannerOpenHandler();
      if (!attached && attempt < 20) {
        this.schedulePlannerOpenHandlerAttach(attempt + 1);
      }
    }, attempt === 0 ? 0 : 100);
  }

  private attachPlannerOpenHandler(): boolean {
    const chartHost = this.hostRef.nativeElement.querySelector('.chart-host') as HTMLDivElement | null;
    const chart = chartHost ? echarts.getInstanceByDom(chartHost) : null;
    if (!chart) {
      return false;
    }

    this.detachPlannerOpenHandler();
    chart.off('click');
    this.plannerClickHandler = (params: any) => {
      if (params?.componentType !== 'series') {
        return;
      }

      const panelConfig = this.data?.chartConfig?.plannerPanel || {};
      const sourceRow = params?.data?.raw || null;
      const requestParams = this.buildPlannerPanelRequestData(sourceRow, panelConfig);
      this.pendingPlannerHighlightRequest = this.cloneRequestParams(requestParams);
      window.dispatchEvent(new CustomEvent('pat-price-planner-open', {
        detail: {
          chartWidgetId: panelConfig.chartWidgetId || this.data?.widgetId,
          plannerWidgetId: panelConfig.plannerWidgetId || 'widget-price-planner',
          chartWidth: panelConfig.chartWidth ?? 8,
          plannerWidth: panelConfig.plannerWidth ?? 4,
          requestFieldMap: panelConfig.requestFieldMap || null,
          requestParams,
          sourceRow,
          category: params?.name,
          seriesName: params?.seriesName,
        },
      }));
    };

    chart.on('click', this.plannerClickHandler);
    return true;
  }

  private detachPlannerOpenHandler(): void {
    if (!this.plannerClickHandler) {
      return;
    }

    const chartHost = this.hostRef.nativeElement.querySelector('.chart-host') as HTMLDivElement | null;
    const chart = chartHost ? echarts.getInstanceByDom(chartHost) : null;
    chart?.off('click', this.plannerClickHandler);
    this.plannerClickHandler = undefined;
  }

  private renderStatusPopover(configUrl: string, rowConfig: any, row: any, cell: any): void {
    const token = ++this.renderToken;
    const cached = this.hoverConfigCache.get(configUrl);

    if (cached) {
      this.renderHoverWidget(cached, rowConfig, row, cell, token);
      return;
    }

    this.hoverConfigService.getConfig(configUrl, {}, {}, 'get')?.subscribe({
      next: (dashboardConfig: any) => {
        this.hoverConfigCache.set(configUrl, dashboardConfig);
        this.renderHoverWidget(dashboardConfig, rowConfig, row, cell, token);
      },
      error: () => this.hideStatusPopover(),
    });
  }

  private renderHoverWidget(dashboardConfig: any, rowConfig: any, row: any, cell: any, token: number): void {
    if (token !== this.renderToken || !this.activeStatusPopover) {
      return;
    }

    const widget = this.prepareHoverWidget(this.resolveHoverWidget(dashboardConfig), rowConfig, row, cell);
    if (!widget || !this.statusPopoverHost) {
      this.hideStatusPopover();
      return;
    }

    this.statusPopoverHost.clear();
    this.chartRendererService.renderChart(widget.type, this.statusPopoverHost, {
      ...widget,
      widgetId: `${widget.widgetId || widget.type}-status-hover`,
      hoverContext: {
        rowLabel: row?.label,
        status: cell?.value,
        category: cell?.category,
        sourceRow: cell?.source || null,
      },
      height: this.resolveWidgetHeight(rowConfig?.hoverPanel?.height, widget.height),
    });
  }

  private prepareHoverWidget(widget: any, rowConfig: any, row: any, cell: any): any {
    if (!widget || !rowConfig?.hoverApi) {
      return widget;
    }

    const sourceRow = cell?.source && typeof cell.source === 'object' ? cell.source : null;
    const requestData = this.buildHoverApiRequestData(sourceRow, rowConfig.hoverApi);
    if (!sourceRow || !requestData) {
      return widget;
    }

    const hoverApi = rowConfig.hoverApi;
    const version = String(hoverApi.version || widget.version || '1.0');

    return {
      ...widget,
      type: hoverApi.type || widget.type,
      apiUrl: hoverApi.apiUrl || widget.apiUrl,
      method: hoverApi.method || widget.method || 'post',
      skipDefaultPayload: true,
      customPayload: {
        requestData,
        responseList: [],
        entity: hoverApi.entity || 'engine_level_repower',
        batchId: hoverApi.batchId || null,
        workflowId: hoverApi.workflowId || 'default_workflow',
        endpoints: [
          {
            functionName: hoverApi.functionName || widget.functionName || 'DefaultService',
            version,
          },
        ],
        communicationType: hoverApi.communicationType || 'rest',
        version,
      },
      version,
      functionName: hoverApi.functionName || widget.functionName,
      entity: hoverApi.entity || widget.entity,
      workflowId: hoverApi.workflowId || widget.workflowId,
    };
  }

  private buildHoverApiRequestData(sourceRow: any, hoverApi: any): any {
    if (!sourceRow || typeof sourceRow !== 'object') {
      return null;
    }

    const fieldMap = hoverApi?.requestFieldMap && typeof hoverApi.requestFieldMap === 'object'
      ? hoverApi.requestFieldMap
      : {
          customer_name: 'customer_name',
          equipment: 'equipment',
          engine_model_key: 'engine',
          emission_key: 'emission',
        };

    const readValue = (targetKey: string): any => {
      const mappedPath = String(fieldMap?.[targetKey] || targetKey);
      return mappedPath.split('.').reduce((current: any, key: string) => current?.[key], sourceRow);
    };

    const requestShape = String(hoverApi?.requestShape || 'flat').toLowerCase();
    const customerName = readValue('customer_name');
    const equipment = readValue('equipment');
    const engineModelKey = readValue('engine_model_key');
    const emissionKey = readValue('emission_key');

    if (requestShape === 'pricedynamicsfilter') {
      if (!this.hasValue(customerName) || !this.hasValue(equipment) || !this.hasValue(engineModelKey) || !this.hasValue(emissionKey)) {
        return null;
      }

      return {
        filter: {
          startDate: String(hoverApi?.startDate || '01-01-2021'),
          endDate: String(hoverApi?.endDate || '12-31-2025'),
          machine_model: equipment,
          engine_model_key: `${engineModelKey},${emissionKey}`,
          customer_name: customerName,
        },
      };
    }

    if (requestShape === 'filter') {
      if (!this.hasValue(customerName) || !this.hasValue(equipment) || !this.hasValue(engineModelKey) || !this.hasValue(emissionKey)) {
        return null;
      }

      return {
        filter: {
          machine_model: equipment,
          engine_model: engineModelKey,
          emission: emissionKey,
          customer_name: customerName,
        },
      };
    }

    const customerNameArray = this.toRequestArray(customerName);
    const equipmentArray = this.toRequestArray(equipment);
    const engineModelKeyArray = this.toRequestArray(engineModelKey);
    const emissionKeyArray = this.toRequestArray(emissionKey);

    if (!customerNameArray.length || !equipmentArray.length || !engineModelKeyArray.length || !emissionKeyArray.length) {
      return null;
    }

    return {
      customer_name: customerNameArray,
      equipment: equipmentArray,
      engine_model_key: engineModelKeyArray,
      emission_key: emissionKeyArray,
    };
  }

  private toRequestArray(value: any): any[] {
    if (Array.isArray(value)) {
      return value
        .flat(Infinity)
        .filter((item: any) => item !== undefined && item !== null && String(item).trim() !== '');
    }

    if (value === undefined || value === null || String(value).trim() === '') {
      return [];
    }

    return [value];
  }

  private hasValue(value: any): boolean {
    if (Array.isArray(value)) {
      return value.flat(Infinity).some((item: any) => item !== undefined && item !== null && String(item).trim() !== '');
    }

    return value !== undefined && value !== null && String(value).trim() !== '';
  }

  private resolveHoverWidget(dashboardConfig: any): any {
    const rows = Array.isArray(dashboardConfig?.pageContent) ? dashboardConfig.pageContent : [];

    for (const row of rows) {
      const columns = Array.isArray(row?.columns) ? row.columns : [];
      const widget = columns.find((column: any) => column?.type);
      if (widget) {
        return widget;
      }
    }

    return null;
  }

  private resolveStatusRowConfig(row: any): any {
    const configuredRows = this.data?.chartConfig?.statusRows || this.data?.chartConfig?.metricsRows || this.data?.statusRows || [];
    if (!Array.isArray(configuredRows)) {
      return null;
    }

    return configuredRows.find((entry: any) => {
      const label = String(entry?.label || entry?.name || '').trim().toLowerCase();
      const valueKey = String(entry?.valueKey || entry?.key || '').trim().toLowerCase();
      return label === String(row?.label || '').trim().toLowerCase() ||
        valueKey === String(row?.valueKey || '').trim().toLowerCase();
    });
  }

  private getStatusHoverConfigUrl(rowConfig: any): string {
    return String(
      rowConfig?.hoverDashboardConfigUrl ||
      rowConfig?.hoverConfigUrl ||
      rowConfig?.dashboardConfigUrl ||
      '',
    ).trim();
  }

  private resolveWidgetHeight(panelHeight: any, widgetHeight: any): any {
    if (widgetHeight) {
      return widgetHeight;
    }

    const numericPanelHeight = Number(panelHeight);
    return Number.isFinite(numericPanelHeight) && numericPanelHeight > 80
      ? `${Math.max(120, numericPanelHeight - 96)}px`
      : undefined;
  }

  private buildPopoverStyle(event: MouseEvent, panel: any): Record<string, string> {
    const width = Number(panel?.width ?? 590);
    const height = Number(panel?.height ?? 460);
    const margin = 16;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const targetRect = (event.currentTarget as HTMLElement | null)?.getBoundingClientRect?.();
    const cardRect = this.hostRef.nativeElement
      .querySelector('.grouped-bar-status-card')
      ?.getBoundingClientRect?.();
    const anchorX = targetRect ? targetRect.left + (targetRect.width / 2) : event.clientX;
    const anchorTop = targetRect?.top ?? event.clientY;
    const anchorBottom = targetRect?.bottom ?? event.clientY;
    const leftBoundary = Math.max(margin, (cardRect?.left ?? 0) + margin);
    const rightBoundary = Math.min(viewportWidth - margin, (cardRect?.right ?? viewportWidth) - margin);
    const availableWidth = Math.max(0, rightBoundary - leftBoundary);
    const sideOffset = 24;
    const centeredLeft = anchorX - (width / 2);
    const biasedLeft = anchorX < leftBoundary + (width / 2)
      ? anchorX + sideOffset
      : anchorX > rightBoundary - (width / 2)
        ? anchorX - width - sideOffset
        : centeredLeft;
    const minLeft = availableWidth >= width ? leftBoundary : margin;
    const maxLeft = availableWidth >= width
      ? rightBoundary - width
      : Math.max(margin, viewportWidth - width - margin);
    const left = Math.min(Math.max(biasedLeft, minLeft), maxLeft);
    const preferredTop = anchorTop - height - 12;
    const fallbackTop = anchorBottom + 12;
    const top = preferredTop >= margin
      ? preferredTop
      : Math.min(fallbackTop, Math.max(margin, viewportHeight - height - margin));

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  }
}
